// Package Imports
import { message } from "antd";
import React, { useState, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useAuthStore } from "../../../store";

// Internal Imports
import AuthForm from "../../../components/forms/AuthForm";
import { BodyOne, HeadingOne } from "../../../components/typography";
import BlueButton from "../../../components/buttons/BlueButton";
import AccountTypeBlock from "../../../components/buttons/AccountTypeBlock";
import { Error } from "../../../components/feedback";
import {
  // assign_user_to_usertype_group,
  // assign_to_usertype_group,
  // create_new_user_organization,
  create_new_user_organization_v2,
  check_existing_organization_v2,
  add_org_user_type,
  adminLogin
} from "../../../services/AuthActions";
import { UserException, populateSessionStorage } from "../../../utils/helpers";
import { useSSOData, useAddUserToGroup } from "../../../hooks";
import SupportBlock from "../../../components/info/SupportBlock";
import mixpanelService from "../../../services/mixpanel";
import bugfenderHelper from "../../../services/bugfender";
import { environments } from "../../../constants";
import { ESessionStorageKeys } from "../../../../../types";


/**
 * Organization type selection and org creation at the end of signup.
 * @param {{ prevStep: Function, formChangeHandler: Function, setStepState: Function }} props
 */
const AccountTypeForm = ({
  prevStep,
  formChangeHandler,
  setStepState,
}) => {
  const navigate = useNavigate();
  const { validSSOData } = useSSOData();
  const [isMobile, setIsMobile] = useState(false);
  const { handleSubmit, control, formState: { errors } } = useForm();
  const [loadingState, setLoadingState] = useState(false);
  const signUpFormInputs = useAuthStore((s) => s.signUpInputs);
  const loginToken = useAuthStore((s) => s.loginTokens);

  const { handleAddUserToGroup, loading: addUserToGroupLoading } = useAddUserToGroup({
    onSuccessCallback: () => {
      // console.log("User successfully added to group");
    },
    onErrorCallback: (error) => {
      throw UserException("Failed to add user to Cognito Group [AccountTypeForm]");
    }
  });

  const orgExistingCheck = async (formData) => {
    const companyCheck = await check_existing_organization_v2(formData);

    // CHECK - Company Check
    if (companyCheck === null) {
      bugfenderHelper.error("Failed to check if organization exists");
      throw UserException("check_existing_organization_v2 - Failed to check if organization exists");
    }

    // Status 902 === Organization Exists
    // Status 903 === Organization Does-Not Exist
    if (companyCheck.status !== "903") {
      message.error(companyCheck.description);
      bugfenderHelper.warn(companyCheck.description);
      throw UserException(companyCheck.description);
    }
  };

  // MAIN - Sign-Up Flow
  const userAuth = async (formData) => {
    try {
      const email = signUpFormInputs?.email.toLowerCase();
      const password = signUpFormInputs?.password;
      const clientId = environments.REACT_APP_DEPLOY_USER_POOL_WEB_CLIENT_ID;

      // CHECK - Email & Password
      if (!email || !password) {
        throw UserException("Email & Password are required");
      }

      // Check if org exists
      await orgExistingCheck({
        ...signUpFormInputs,
        ...formData
      });

      // Add user to Cognito Group
      const user_group_add = await handleAddUserToGroup({
        username: signUpFormInputs?.username,
        groupName: formData?.user_type
      });

      // CHECK - User was added to Cognito Group
      if (!user_group_add?.adminAddUserToGroup?.text) {
        bugfenderHelper.error("handleAddUserToGroup - Failed to add user to Cognito Group [AccountTypeForm]")
        throw UserException("handleAddUserToGroup - Failed to add user to Cognito Group [AccountTypeForm]");
      }

      // IF - User was added to Cognito Group
      if (user_group_add) {
        // Create New Ogranization & Return Data | Organization ID, Created/Updated Timestamps
        // let createdOrgData = await create_new_user_organization({
        //   ...signUpFormInputs,
        //   ...loginToken,
        //   ...formData
        // });

        const adminLoginResult = await adminLogin({
          username: email,
          password,
          clientId
        });

        // CHECK - Admin Login Result
        if (adminLoginResult === null) {
          bugfenderHelper.error("adminLogin - Failed to login as admin")
          throw UserException("adminLogin - Failed to login as admin");
        }

        const rePopulateAuthData = await populateSessionStorage([
          {
            key: ESessionStorageKeys.AUTH_TOKEN,
            value: adminLoginResult.AccessToken
          },
          {
            key: ESessionStorageKeys.REFRESH_TOKEN,
            value: adminLoginResult.RefreshToken
          },
          {
            key: ESessionStorageKeys.ID_TOKEN,
            value: adminLoginResult.IdToken
          },
        ]);

        // CHECK - Re-Populate Auth Data
        if (rePopulateAuthData === null) {
          throw UserException("populateSessionStorage - Failed to re-populate auth data");
        }

        const memberData = {
          [signUpFormInputs?.username]: {
            created_timestamp: new Date().toISOString(),
            updated_timestamp: new Date().toISOString(),
            email: signUpFormInputs?.email,
            first_name: signUpFormInputs?.firstName,
            last_name: signUpFormInputs?.lastName,
            gender: signUpFormInputs?.gender,
            org_role: "Super Admin",
            phone_number: signUpFormInputs?.phoneNumber,
          }
        }

        let createdOrgData = await create_new_user_organization_v2({
          ...signUpFormInputs,
          ...loginToken,
          ...formData
        }, memberData);

        // CHECK - Organization was created
        if (createdOrgData === null) {
          bugfenderHelper.error("create_new_user_organization - Failed to create new organization")
          throw UserException("create_new_user_organization - Failed to create new organization");
        }

        // Re-Construct Data Object & Only Include Required Data
        createdOrgData = {
          org_id: createdOrgData.org_id,
          sub: createdOrgData.sub,
          created_timestamp: createdOrgData.created_timestamp,
          updated_timestamp: createdOrgData.updated_timestamp,
        };

        const addOrgData = {
          org_id: createdOrgData?.org_id,
          registration_country: signUpFormInputs?.registration_country,
          org_name: signUpFormInputs?.org_name,
          org_trading_name: signUpFormInputs?.org_trading_name,
          user_type: formData?.user_type,
          firstname: signUpFormInputs?.firstName,
          lastname: signUpFormInputs?.lastName,
          email: signUpFormInputs?.email.toLowerCase(),
          cellphone: signUpFormInputs?.phoneNumber,
        }

        const addOrgDataResult = await add_org_user_type(addOrgData, loginToken.jwt);

        // CHECK - User Data updated in Organization
        if (addOrgDataResult === null) {
          bugfenderHelper.error("add_org_user_type - Failed to add user to organization")
          throw UserException("add_org_user_type - Failed to add user to organization");
        }

        // Add OrgData to Global State
        await formChangeHandler(createdOrgData, "user");

        const userData = JSON.stringify({
          token: loginToken.jwt,
          refresh: loginToken.refresh,
          sub: createdOrgData.sub,
          org_id: createdOrgData.org_id
        });

        message.success("You're All Setup, Woohoo...");

        navigate(`/signup-redirect/mobile-app/${userData}`);
      }
    } catch (err) {
      setLoadingState(false);
      bugfenderHelper.error("Account Setup Process Failed")
      message.error("Account Setup Process Failed");
      console.error("userAuth [ERROR]\n", err);
      setStepState(1);
    }
  };

  const onSubmit = async (formData) => {
    try {
      setLoadingState(true);

      // Add User_Type to Global State
      await formChangeHandler(formData, "user");

      // Invoke Sign-Up Flow here
      await userAuth(formData);

      // Invoke Post-Sign-Up Flow
      // await postSignUp(formData);

      setLoadingState(false);
    } catch (error) {
      setLoadingState(false);
      bugfenderHelper.error("Unable to register new organisation");
      Error({
        message: "Something went wrong...",
        description: "Please contact our support services",
      });
    }
  };

  // Combined loading state
  const isLoading = loadingState || addUserToGroupLoading;

  // SIDE-EFFECT - Check SSO Data for App-Type
  useLayoutEffect(() => {
    // CHECK - SSO Data is Valid
    if (!validSSOData) return;

    const processAppType = async () => {
      try {
        const appType = validSSOData.app_type;

        // CHECK - App Type is Mobile
        if (appType === "mobile") {
          setIsMobile(true);

        }
      } catch (err) {
        console.error("processAppType [ERROR]", err);
      }
    };

    mixpanelService.track('App.Register.Confirm', {
      sign_up_inputs: signUpFormInputs,
      form_data: signUpFormInputs,
      device_type: isMobile ? "mobile" : "web"
    });

    processAppType();
  }, [validSSOData]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AuthForm>
        <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
          Type of Organization
        </HeadingOne>
        <Controller
          name="user_type"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <AccountTypeBlock onChange={onChange} value={value} isMobile={isMobile} />
          )}
        />
        {errors.user_type && (
          <BodyOne className="text-red-600 mt-2">
            Please select an organization type
          </BodyOne>
        )}
        <div className="grid grid-cols-2 gap-3 mt-6">
          <BlueButton
            className="bg-white border border-[#0458f6] text-[#0458f6] flex justify-center items-center hover:bg-[#0458f6] hover:text-white"
            type="button"
            style={{ color: "#0458F6" }}
            onClick={() => prevStep()}
          >
            Back
          </BlueButton>
          <BlueButton
            className="text-white flex justify-center items-center py-2.5 font-semibold hover:text-white"
            type="submit"
            isLoading={isLoading}
          >
            Proceed
          </BlueButton>
        </div>
        <p className="text-center mt-5 mb-0 text-xs leading-5 text-slate-500 font-poppins">
          By continuing you agree to our Terms of Service and Privacy Policy
        </p>
        <p className="text-center mt-3 mb-0 text-xs leading-5 text-slate-500 font-poppins">
          Already have an account?{" "}
          <span
            className="text-[#0458f6] font-medium cursor-pointer hover:underline"
            onClick={() =>
              navigate(
                "/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM="
              )
            }
          >
            Login
          </span>
        </p>
        <div className="auth-footer-rule" />
        <SupportBlock area="sign-up-org-type" tailwindProps="mt-0" />
      </AuthForm>
    </form>
  );
};

export default AccountTypeForm;
